import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "../api";
import { useAuth } from "./AuthContext";

const DefaultCompanyContext = createContext(null);

const STORAGE_KEY = "takofy.default_company_id";

export function DefaultCompanyProvider({ children }) {
  const { user } = useAuth();
  const role = String(user?.role || "").toLowerCase();
  const isAdmin = role === "admin";
  const [companies, setCompanies] = useState([]);
  const [defaultCompanyId, setDefaultCompanyIdState] = useState(null);
  const [loading, setLoading] = useState(false);
  const inFlightRef = useRef(null);
  const lastResolvedAtRef = useRef(0);
  const lastUserKeyRef = useRef("");
  const companiesRef = useRef(companies);

  useEffect(() => {
    companiesRef.current = companies;
  }, [companies]);

  const refreshCompanies = useCallback(async () => {
    if (!user) {
      setCompanies([]);
      setDefaultCompanyIdState(null);
      lastResolvedAtRef.current = 0;
      lastUserKeyRef.current = "";
      return;
    }

    if (!isAdmin) {
      const fallbackCompanyId = Number(user?.companyId || 0) || null;
      setCompanies(
        fallbackCompanyId
          ? [{ Id: fallbackCompanyId, Name: user?.companyName || `Sirket ${fallbackCompanyId}`, IsDefault: true }]
          : []
      );
      setDefaultCompanyIdState(fallbackCompanyId);
      lastResolvedAtRef.current = Date.now();
      lastUserKeyRef.current = `${user?.id || 0}:${role}:${fallbackCompanyId || 0}`;
      return;
    }

    const userKey = `${user?.id || 0}:${role}:admin`;
    if (inFlightRef.current) {
      return inFlightRef.current;
    }
    if (
      companiesRef.current.length > 0 &&
      lastUserKeyRef.current === userKey &&
      Date.now() - lastResolvedAtRef.current < 30000
    ) {
      return companiesRef.current;
    }

    const request = (async () => {
      try {
        setLoading(true);
        const companiesRes = await api.get("/companies");

        const items = Array.isArray(companiesRes.data) ? companiesRes.data : [];
        const storedId = Number(window.localStorage.getItem(STORAGE_KEY) || 0) || null;
        const listDefaultId =
          Number(
            items.find((item) => Number(item?.IsDefault || 0) === 1)?.Id || 0
          ) || null;
        const preferredId =
          (storedId && items.some((item) => Number(item.Id) === storedId) && storedId) ||
          listDefaultId ||
          (items[0] ? Number(items[0].Id) : null);

        setCompanies(items);
        setDefaultCompanyIdState(preferredId);
        if (preferredId) {
          window.localStorage.setItem(STORAGE_KEY, String(preferredId));
        } else {
          window.localStorage.removeItem(STORAGE_KEY);
        }
        lastResolvedAtRef.current = Date.now();
        lastUserKeyRef.current = userKey;
        return items;
      } catch {
        setCompanies([]);
        setDefaultCompanyIdState(null);
        return [];
      } finally {
        inFlightRef.current = null;
        setLoading(false);
      }
    })();

    inFlightRef.current = request;
    return request;
  }, [isAdmin, role, user]);

  useEffect(() => {
    void refreshCompanies();
  }, [refreshCompanies]);

  const setDefaultCompanyId = useCallback(
    async (companyId, persist = false) => {
      const normalizedId = Number(companyId || 0) || null;
      setDefaultCompanyIdState(normalizedId);
      if (normalizedId) {
        window.localStorage.setItem(STORAGE_KEY, String(normalizedId));
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }

      if (persist && isAdmin && normalizedId) {
        await api.post(`/companies/${normalizedId}/set-default`);
        await refreshCompanies();
      }
    },
    [isAdmin, refreshCompanies]
  );

  const defaultCompany = useMemo(
    () => companies.find((item) => Number(item.Id) === Number(defaultCompanyId)) || null,
    [companies, defaultCompanyId]
  );

  const value = useMemo(
    () => ({
      companies,
      defaultCompanyId,
      defaultCompany,
      loading,
      isAdmin,
      refreshCompanies,
      setDefaultCompanyId,
    }),
    [companies, defaultCompany, defaultCompanyId, isAdmin, loading, refreshCompanies, setDefaultCompanyId]
  );

  return <DefaultCompanyContext.Provider value={value}>{children}</DefaultCompanyContext.Provider>;
}

export function useDefaultCompany() {
  const value = useContext(DefaultCompanyContext);
  if (!value) {
    throw new Error("useDefaultCompany must be used within DefaultCompanyProvider");
  }
  return value;
}
